import { Component, OnInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { HttpClient, HttpClientModule, HttpParams } from '@angular/common/http';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';

@Component({
  selector: 'app-foreign-college-videos',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HttpClientModule, AgGridAngular],
  templateUrl: './foreign-college-videos.component.html',
  styleUrls: ['./foreign-college-videos.component.scss'],
})
export class ForeignCollegeVideosComponent implements OnInit {
  display = 'none';
  videoForm!: FormGroup;

  countries: any[] = [];
  courses: any[] = [];
  colleges: any[] = [];
  selectedImages: string[] = [];

  videosList: any[] = [];
  previewImage: string | null = null;
  expandedRows: { [key: number]: boolean } = {};
  gridApi: any;

  // track deleting id to optionally disable button UI (not required)
  deletingVideoId: string | number | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.buildForm();
    this.loadVideos();
    this.loadCountries();
  }

  buildForm() {
    this.videoForm = this.fb.group({
      country: ['', Validators.required],
      course: ['', Validators.required],
      college: ['', Validators.required],
      url: ['', [Validators.required]],
    });
  }

  // API Calls
  loadCountries() {
    this.http.get<any>('https://smscap.in/api/country').subscribe((res) => {
      this.countries = res?.data || [];
    });
  }

  onCountryChange(countryId: number) {
    this.http
      .get<any>(`https://smscap.in/api/foreign_course/${countryId}`)
      .subscribe((res) => {
        this.courses = res?.data || [];
        this.colleges = [];
      });
  }

  onCourseChange(courseId: number) {
    const countryId = this.videoForm.value.country;
    this.http
      .get<any>(
        `https://smscap.in/api/foreign_college/${countryId}/${courseId}`
      )
      .subscribe((res) => {
        this.colleges = res?.data || [];
      });
  }

  loadVideos() {
    this.http.get<any>('https://smscap.in/api/foreign_college_video').subscribe(
      (res) => {
        this.videosList = res?.data || [];
        // refresh grid renderers if gridApi exists
        if (this.gridApi && (this.gridApi as any).refreshCells) {
          try {
            (this.gridApi as any).refreshCells({ force: true });
          } catch (e) {
            // ignore
          }
        }
      },
      (err) => {
        console.error('loadVideos error', err);
        this.videosList = [];
      }
    );
  }

  onFilesSelected(event: any) {
    const files: FileList = event.target.files;
    this.selectedImages = [];
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedImages.push(e.target.result); // base64 preview
      };
      reader.readAsDataURL(file);
    });
  }

  addVideo() {
    if (this.videoForm.invalid || this.selectedImages.length === 0) return;

    const payload = {
      foreign_college_id: this.videoForm.value.college,
      videos: this.selectedImages.map((img) => ({
        image: img,
        url: this.videoForm.value.url,
      })),
    };

    this.http
      .post('https://smscap.in/api/foreign_college_video', payload)
      .subscribe({
        next: () => {
          this.loadVideos();
          this.onCloseHandled();
        },
        error: (err) => {
          console.error('Upload failed:', err);
        },
      });
  }

  openAdd() {
    this.display = 'block';
  }

  onCloseHandled() {
    this.display = 'none';
    this.videoForm.reset();
    this.selectedImages = [];
  }

  openImagePreview(imagePath: string) {
    this.previewImage = `https://smscap.in/${imagePath}`;
  }

  closeImagePreview() {
    this.previewImage = null;
  }

  // Grid
  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  // -------------------------
  // Column definitions (with delete overlay)
  // -------------------------
  columnDefs: ColDef[] = [
    {
      headerName: 'College & Videos',
      field: 'videos',
      flex: 1,
      autoHeight: true,
      cellRenderer: (params: any) => {
        const collegeId =
          params.data?.foreign_college_id ??
          params.data?.college_id ??
          params.data?.id;
        const collegeName = params.data?.college_name ?? 'College';
        const videos = params.value || [];
        const expanded = this.expandedRows[collegeId] || false;

        // Main Card
        const card = document.createElement('div');
        card.style.border = '1px solid #eee';
        card.style.borderRadius = '12px';
        card.style.padding = '15px';
        card.style.margin = '10px';
        card.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
        card.style.background = '#fff';

        // Title
        const title = document.createElement('h5');
        title.innerText = collegeName;
        title.style.fontWeight = '600';
        title.style.margin = '0 0 12px 0';
        card.appendChild(title);

        // Videos container
        const vidContainer = document.createElement('div');
        vidContainer.style.display = 'flex';
        vidContainer.style.flexWrap = 'wrap';
        vidContainer.style.gap = '10px';

        // Show only first 8 unless expanded
        const videosToShow = expanded ? videos : videos.slice(0, 8);

        videosToShow.forEach((v: any) => {
          if (!v?.image) return;

          const vidBox = document.createElement('div');
          vidBox.className = 'video-box';
          vidBox.style.width = '120px';
          vidBox.style.height = '120px';
          vidBox.style.borderRadius = '10px';
          vidBox.style.overflow = 'hidden';
          vidBox.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
          vidBox.style.cursor = 'pointer';
          vidBox.style.display = 'flex';
          vidBox.style.flexDirection = 'column';
          vidBox.style.alignItems = 'center';
          vidBox.style.justifyContent = 'center';
          vidBox.style.background = '#fafafa';
          vidBox.style.position = 'relative'; // required for delete overlay

          // Thumbnail
          const img = document.createElement('img');
          img.src = `https://smscap.in/${v.image}`;
          img.style.width = '100%';
          img.style.height = '80%';
          img.style.objectFit = 'cover';
          img.style.borderBottom = '1px solid #eee';

          // Preview when clicked
          img.addEventListener('click', (ev) => {
            ev.stopPropagation();
            this.ngZone.run(() => this.openImagePreview(v.image));
          });

          // Delete button (bottom-right) — transparent background, red trash icon
          const delBtn = document.createElement('button');
          delBtn.className = 'video-delete-btn';
          delBtn.type = 'button';
          delBtn.title = 'Delete video';
          delBtn.setAttribute('aria-label', 'Delete video');
          delBtn.innerHTML = '<i class="fa fa-trash" aria-hidden="true"></i>';

          // inline fallback positioning
          delBtn.style.position = 'absolute';
          delBtn.style.bottom = '6px';
          delBtn.style.right = '6px';
          delBtn.style.zIndex = '5';
          delBtn.style.background = 'transparent';
          delBtn.style.border = 'none';
          delBtn.style.padding = '2px';
          delBtn.style.cursor = 'pointer';

          delBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const vidId = v.id ?? v.video_id ?? v.college_video_id ?? v.videoId;
            this.ngZone.run(() => {
              if (
                params.context &&
                params.context.componentParent &&
                params.context.componentParent.deleteVideo
              ) {
                params.context.componentParent.deleteVideo(
                  vidId,
                  params.data,
                  v
                );
              } else {
                this.deleteVideo(vidId, params.data, v);
              }
            });
          });

          // Video link
          const link = document.createElement('a');
          link.href = v.url || '#';
          link.target = '_blank';
          link.innerText = 'Watch Video';
          link.style.fontSize = '12px';
          link.style.color = '#007bff';
          link.style.textDecoration = 'none';
          link.style.marginTop = '5px';

          vidBox.appendChild(img);
          vidBox.appendChild(link);
          vidBox.appendChild(delBtn);
          vidContainer.appendChild(vidBox);
        });

        card.appendChild(vidContainer);

        // See More button if >8
        if (videos.length > 8) {
          const toggle = document.createElement('button');
          toggle.innerText = expanded ? 'See Less' : 'See More';
          toggle.style.background = 'none';
          toggle.style.border = 'none';
          toggle.style.color = 'blue';
          toggle.style.cursor = 'pointer';
          toggle.style.marginTop = '8px';
          toggle.style.fontWeight = '500';

          toggle.onclick = (e) => {
            e.stopPropagation();
            this.ngZone.run(() => {
              this.expandedRows[collegeId] = !expanded;
              if (this.gridApi && (this.gridApi as any).refreshCells) {
                try {
                  (this.gridApi as any).refreshCells({ force: true });
                } catch (err) {}
              }
            });
          };

          card.appendChild(toggle);
        }

        return card;
      },
    },
  ];

  defaultColDef = { resizable: true, sortable: true };

  // -------------------------
  // Delete video (reloads list on success)
  // -------------------------
  deleteVideo(videoId: any, parentRow: any, videoObj?: any) {
    const resolvedId =
      videoId ??
      videoObj?.id ??
      videoObj?.video_id ??
      videoObj?.college_video_id ??
      null;
    const targetPath = videoObj?.image ?? null;

    if (!resolvedId && !targetPath) {
      window.alert('Unable to determine video to delete.');
      return;
    }

    if (!window.confirm('Delete this video? This action cannot be undone.'))
      return;

    // optional: show deleting state
    this.deletingVideoId = resolvedId ?? targetPath;

    const params = new HttpParams().set('id', String(resolvedId ?? ''));
    this.http
      .delete('https://smscap.in/api/foreign_college_video', { params })
      .subscribe({
        next: () => {
          // reload fresh data from server immediately
          this.loadVideos();
          this.deletingVideoId = null;
        },
        error: (err) => {
          console.error('deleteVideo error', err);
          this.deletingVideoId = null;
          window.alert('Failed to delete video. See console for details.');
        },
      });
  }
}
